import React, { useCallback, useMemo } from "react";

import { Button, ChevronIcon } from "@packages/ds";

import * as Dropdown from "ds/Dropdown";
import { formatNumber } from "src/organization/pages/project/environment/metrics/logs/helper";

import * as S from "./FilterSelect.styles";
import { FilterSelectValue, FilterSelectOption, FilterMode } from "./types";

const defaultValue: FilterSelectValue = {
  mode: FilterMode.Subtractive,
  values: []
};

function selectionSummary(
  options: FilterSelectOption[],
  value: FilterSelectValue
): string {
  if (value.mode === FilterMode.Additive) {
    if (options.length === value.values.length) {
      return "All";
    }

    return `${value.values.length}`;
  }

  if (value.values.length === 0) {
    return "All";
  }

  return `-${value.values.length}`;
}

export type FilterSelectProps = {
  title: string;
  value?: FilterSelectValue;
  options: FilterSelectOption[];
  onChange?: (fsv: FilterSelectValue) => void;
};

const FilterSelect = ({
  title,
  value = defaultValue,
  options,
  onChange = () => {}
}: FilterSelectProps) => {
  const isChecked = useCallback(
    optionValue => {
      if (value.mode === FilterMode.Subtractive) {
        return value.values.length === 0 || !value.values.includes(optionValue);
      }

      return value.values.includes(optionValue);
    },
    [value]
  );

  const handleClickSwitchToAdditive = useCallback(
    selectedValue => {
      if (!onChange) {
        return;
      }

      onChange({ mode: FilterMode.Additive, values: [selectedValue] });
    },
    [onChange]
  );

  const handleClickSwitchToSubtractive = useCallback(() => {
    if (!onChange) {
      return;
    }
    onChange({ mode: FilterMode.Subtractive, values: [] });
  }, [onChange]);

  const selectionButton = useMemo(() => {
    if (value.values.length === 0) {
      return null;
    }

    if (
      value.mode === FilterMode.Additive &&
      options.length === value.values.length
    ) {
      return null;
    }

    return (
      <Button variant="secondary" onClick={handleClickSwitchToSubtractive}>
        Select All
      </Button>
    );
  }, [
    value.values.length,
    value.mode,
    options.length,
    handleClickSwitchToSubtractive
  ]);

  const handleChange = useCallback(
    optionValue => {
      if (!onChange) {
        return;
      }

      if (!value.values.includes(optionValue)) {
        const newValue = {
          mode: value.mode,
          values: [...value.values, optionValue]
        };
        if (
          newValue.mode === FilterMode.Additive &&
          options.length === newValue.values.length
        ) {
          newValue.mode = FilterMode.Subtractive;
          newValue.values = [];
        }
        onChange(newValue);
      } else {
        const newValue = {
          mode: value.mode,
          values: value.values.filter(v => v !== optionValue)
        };
        onChange(newValue);
      }
    },
    [onChange, options.length, value.mode, value.values]
  );

  function displayCheckboxLabel(v: FilterSelectOption, displayCount: boolean) {
    const label = v.label ?? v.value;

    return (
      <>
        {value.mode === FilterMode.Additive &&
        value.values.includes(v.value) ? (
          <S.CheckboxLabel
            data-testid="checkbox-line-all"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleClickSwitchToSubtractive();
            }}
          >
            {!label ? (
              <S.CheckboxLabelEmpty>{"<empty>"}</S.CheckboxLabelEmpty>
            ) : (
              <S.CheckboxLabelValue title={label}>{label}</S.CheckboxLabelValue>
            )}
            <S.CheckboxLabelGroup>
              <S.CheckboxLabelMode>{"All"}</S.CheckboxLabelMode>
              <S.CheckboxLabelCount>
                {displayCount ? formatNumber({ value: v.count }) : "-"}
              </S.CheckboxLabelCount>
            </S.CheckboxLabelGroup>
          </S.CheckboxLabel>
        ) : (
          <S.CheckboxLabel
            data-testid="checkbox-line-only"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleClickSwitchToAdditive(v.value);
            }}
          >
            {!label ? (
              <S.CheckboxLabelEmpty>{"<empty>"}</S.CheckboxLabelEmpty>
            ) : (
              <S.CheckboxLabelValue title={label}>{label}</S.CheckboxLabelValue>
            )}
            <S.CheckboxLabelGroup>
              <S.CheckboxLabelMode>{"Only"}</S.CheckboxLabelMode>
              <S.CheckboxLabelCount>
                {displayCount ? formatNumber({ value: v.count }) : "-"}
              </S.CheckboxLabelCount>
            </S.CheckboxLabelGroup>
          </S.CheckboxLabel>
        )}
      </>
    );
  }

  return (
    <Dropdown.Root>
      <Dropdown.Trigger variant="outline">
        {title} ({selectionSummary(options, value)}) <ChevronIcon />
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content>
          <Dropdown.Label>
            {title} ({options.length}) {selectionButton}
          </Dropdown.Label>
          {options.map(o => {
            const checked = isChecked(o.value);

            return (
              <S.CheckboxLine key={o.value}>
                <Dropdown.CheckboxItem
                  style={{ flex: 1 }}
                  disabled={false}
                  checked={checked}
                  onClick={e => {
                    e.preventDefault();
                    handleChange(o.value);
                  }}
                >
                  {displayCheckboxLabel(o, checked)}
                </Dropdown.CheckboxItem>
              </S.CheckboxLine>
            );
          })}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export default FilterSelect;
